import useHelpers from "composables/useHelpers.js";
import axios from "axios";
import { useLearningObjectivesStore } from "store/modules/learningObjectivesStore";
import { storeToRefs } from "pinia";
import { cloneDeep } from "lodash";

export const DEFAULT_LIBRARY_CLUSTER = Object.freeze({
  id: null,
  type: "library_cluster",
  school_id: null,
  name: "",
  description: "",
  shortage: "",
  subjects: [],
  education_year_levels: [],
  editable: false,
  attachments: [],
  learning_objectives: [],
  library_learning_objective_ids: [],
  links: [],
  user: {},
  videos: [],
  trashed: false,
  meta: {
    can_update: true,
    can_delete: true,
  },
});

export class LibraryCluster {
  constructor(data = {}) {
    this.id = data.id ?? DEFAULT_LIBRARY_CLUSTER.id;
    this.type = data.type ?? DEFAULT_LIBRARY_CLUSTER.type;
    this.school_id = data.school_id ?? DEFAULT_LIBRARY_CLUSTER.school_id;
    this.name = data.name ?? DEFAULT_LIBRARY_CLUSTER.name;
    this.description = data.description ?? DEFAULT_LIBRARY_CLUSTER.description;
    this.shortage = data.shortage ?? DEFAULT_LIBRARY_CLUSTER.shortage;
    this.subjects = cloneDeep(
      data.subjects ?? DEFAULT_LIBRARY_CLUSTER.subjects,
    );
    this.education_year_levels = cloneDeep(
      data.education_year_levels ??
        DEFAULT_LIBRARY_CLUSTER.education_year_levels,
    );
    this.editable = data.editable ?? DEFAULT_LIBRARY_CLUSTER.editable;
    this.attachments = cloneDeep(
      data.attachments ?? DEFAULT_LIBRARY_CLUSTER.attachments,
    );
    this.learning_objectives = cloneDeep(
      data.learning_objectives ?? DEFAULT_LIBRARY_CLUSTER.learning_objectives,
    );
    this.library_learning_objective_ids = cloneDeep(
      data.library_learning_objective_ids ??
        DEFAULT_LIBRARY_CLUSTER.library_learning_objective_ids,
    );
    this.links = cloneDeep(data.links ?? DEFAULT_LIBRARY_CLUSTER.links);
    this.user = cloneDeep(data.user ?? DEFAULT_LIBRARY_CLUSTER.user);
    this.videos = cloneDeep(data.videos ?? DEFAULT_LIBRARY_CLUSTER.videos);
    this.trashed = data.trashed ?? DEFAULT_LIBRARY_CLUSTER.trashed;
    this.meta = cloneDeep(data.meta ?? DEFAULT_LIBRARY_CLUSTER.meta);
  }

  hasChanges() {
    return (
      this.name !== DEFAULT_LIBRARY_CLUSTER.name ||
      this.description !== DEFAULT_LIBRARY_CLUSTER.description ||
      this.shortage !== DEFAULT_LIBRARY_CLUSTER.shortage ||
      this.subjects.length !== DEFAULT_LIBRARY_CLUSTER.subjects.length ||
      this.education_year_levels.length !==
        DEFAULT_LIBRARY_CLUSTER.education_year_levels.length ||
      this.attachments.length !== DEFAULT_LIBRARY_CLUSTER.attachments.length ||
      this.learning_objectives.length !==
        DEFAULT_LIBRARY_CLUSTER.learning_objectives.length ||
      this.links.length !== DEFAULT_LIBRARY_CLUSTER.links.length ||
      this.videos.length !== DEFAULT_LIBRARY_CLUSTER.videos.length
    );
  }

  async addLearningObjectivesToModel(addedLearningObjectives) {
    const learningObjectivesStore = useLearningObjectivesStore();
    const { postLearningObjectivesToModel } = learningObjectivesStore;

    if (this.id) {
      // Existing cluster, so post the learning objectives to the model
      return await postLearningObjectivesToModel(addedLearningObjectives, this);
    } else {
      // New cluster, so we need to add the learning objectives to the cluster
      this.learning_objectives.push(
        ...addedLearningObjectives.filter(
          (learningObjective) =>
            !this.learning_objectives.some(
              (existingLearningObjective) =>
                existingLearningObjective.id === learningObjective.id,
            ),
        ),
      );
    }
  }

  async store() {
    // No model set so we can't get it from the model.
    const { schoolId } = useHelpers();

    const learningObjectivesStore = useLearningObjectivesStore();
    const { libraryClusters } = storeToRefs(learningObjectivesStore);

    const { rewriteModelToFormData } = useHelpers();

    const formData = rewriteModelToFormData(this);

    try {
      const {
        data: { data: storedCluster },
      } = await axios.post(
        `/api/school/${schoolId}/library-clusters`,
        formData,
      );

      const { learning_objectives, ...clusterData } = storedCluster;
      Object.assign(this, { ...clusterData });

      this.id = storedCluster.id;

      const storedLearningObjectives = this.learning_objectives.length
        ? await this.addLearningObjectivesToModel(this.learning_objectives)
        : [];

      Object.assign(this, storedCluster);
      this.learning_objectives = storedLearningObjectives;
      libraryClusters.value.push(this);

      return this;
    } catch (error) {
      throw error;
    }
  }

  async update() {
    const learningObjectivesStore = useLearningObjectivesStore();
    const { libraryClusters } = storeToRefs(learningObjectivesStore);

    const { rewriteModelToFormData } = useHelpers();

    try {
      const formData = rewriteModelToFormData(this);
      formData.append("_method", "PUT");

      const {
        data: { updatedCluster },
      } = await axios.post(
        `/api/school/${this.school_id}/library-clusters/${this.id}`,
        formData,
      );

      // TODO:
      const updatedLibraryCluster = Object.assign(this, updatedCluster);

      return updatedLibraryCluster;
    } catch (error) {
      throw error;
    }
  }

  async removeLearningObjectiveFromCluster(learningObjective) {
    const learningObjectivesStore = useLearningObjectivesStore();
    const { deleteLearningObjectiveFromLibraryCluster } =
      learningObjectivesStore;

    try {
      if (this.id !== null) {
        await deleteLearningObjectiveFromLibraryCluster({
          clusterId: this.id,
          learningObjectiveId: learningObjective.id,
        });
      }

      this.learning_objectives = this.learning_objectives.filter(
        (lo) => lo.id !== learningObjective.id,
      );

      this.library_learning_objective_ids = this.learning_objectives
        .filter((lo) => lo.id !== learningObjective.id)
        .map((lo) => lo.id);
    } catch (e) {
      throw new Error(e);
    }
  }

  async upsert() {
    // Early return if there are no changes
    if (!this.hasChanges()) {
      return;
    }

    return this.id === null ? await this.store() : await this.update();
  }
}
